import React, { memo, useState, Fragment } from 'react';
import { camelize } from 'humps';
import classnames from 'classnames';

import { Spinner } from '../../../helpers/Spinner';

import style from './style.module.css';

const FirmRollupTableComponent = ({ statistics, isLoading, user, onShareLink }) => {
  const [expanded, setExpanded] = useState({});
  const [loadingCell, setLoadingCell] = useState(null);

  const toggle = (id, currentValue) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !currentValue,
    }));
  };

  const columns = statistics?.get('columns');
  const outreachColumns = columns?.filter(column => column.get('group') === 'outreach');
  const leadProgressionColumns = columns?.filter(column => column.get('group') === 'lead_progression');
  const approachesColumns = outreachColumns?.filter(column => column.get('key').startsWith('approaches'));
  const outreachLeafColumns = outreachColumns?.filter(column => !column.get('key').startsWith('approaches'));
  const sortedColumns = [
    ...(approachesColumns ?? []),
    ...(outreachLeafColumns ?? []),
    ...(leadProgressionColumns ?? []),
  ];
  const projectTotal = statistics?.get('projectTotal');

  const renderRow = (module, level = 0, parentDefaultExpanded = false, parentRowId = '') => {
    const type = module.get('type');

    const naturalKey =
      type === 'module'
        ? module.getIn(['owner', 'id'])
        : type === 'person'
        ? module.get('membershipId') ?? module.get('userId')
        : module.get('dealmakerId') ?? module.get('directorUserId');
    const rowId = parentRowId ? `${parentRowId}>${type}-${naturalKey}` : `${type}-${naturalKey}`;

    const name =
      type === 'module'
        ? module.getIn(['owner', 'name']) ?? module.get('name')
        : type === 'direct_reports'
        ? 'Individual'
        : module.get('name');
    const children = module.get('children');
    const hasChildren = children?.size > 0;
    const isUserModule = module.getIn(['owner', 'id']) === user?.get('moduleId');
    const isExpanded = rowId in expanded ? expanded[rowId] : isUserModule || parentDefaultExpanded;
    const isInactive = type !== 'module' && Boolean(module.get('inactive'));

    const rowClassName =
      {
        module: 'text-uppercase text-bold name-column row-module',
        primary_dealmaker: 'text-bold name-column',
        secondary_dealmaker: 'text-bold name-column',
        direct_reports: 'text-bold name-column',
        person: '',
      }[type] || '';

    const paddingLevel = type === 'person' ? Math.max(level - 1, 0) : level;

    return (
      <Fragment key={rowId}>
        <tr className={rowClassName || undefined} style={isInactive ? { opacity: 0.3 } : undefined}>
          <td style={{ paddingLeft: `${20 + paddingLevel * 20}px` }}>
            {hasChildren ? (
              <span className="toggle" onClick={() => toggle(rowId, isExpanded)}>
                {isExpanded ? <span className="fa fa-chevron-down" /> : <span className="fa fa-chevron-right" />}
              </span>
            ) : (
              <span className="toggle" />
            )}

            {name}
          </td>

          {sortedColumns.map(column => {
            const metricKey = camelize(column.get('key'));
            const value = module.getIn(['metrics', metricKey]);
            const isShareLink = value > 0;
            const cellClassName = classnames('text-right', 'position-relative', {
              'cursor-pointer': isShareLink,
            });
            const cellKey = `${column.get('key')}-${rowId}`;
            const isCellLoading = loadingCell === cellKey;

            const handleShareLinkDoubleClick = async () => {
              if (!isShareLink) {
                return undefined;
              }

              setLoadingCell(cellKey);
              await onShareLink({
                metric: column.get('key'),
                userId: module.get('userId'),
                userType: type,
                moduleId: module.get('moduleId') ?? module.get('directorUserId') ?? null,
                dealmakerType: module.get('dealmakerType') ?? null,
                dealmakerId: module.get('dealmakerId') ?? null,
              });
              setLoadingCell(null);
            };

            return (
              <td key={column.get('key')} className={cellClassName} onDoubleClick={handleShareLinkDoubleClick}>
                {isCellLoading && (
                  <div className="position-absolute" style={{ left: '35px', top: '25px' }}>
                    <Spinner size="sm" />
                  </div>
                )}
                {value ?? '—'}
              </td>
            );
          })}
        </tr>

        {hasChildren &&
          isExpanded &&
          children.map(child => renderRow(child, level + 1, isUserModule || parentDefaultExpanded, rowId))}
      </Fragment>
    );
  };

  return (
    <div className="position-relative">
      {isLoading && <Spinner />}
      <table className="table table-blue table-hover mb0 statistic-report-table">
        <thead className={style.stickyHeader}>
          <tr>
            <th rowSpan="3">Module / Dealmaker / Individual</th>
            <th colSpan={outreachColumns?.size ?? 0}>
              Outreach
              <span className="ml5 label label-primary font-size-10">Top of funnel</span>
            </th>
            <th colSpan={leadProgressionColumns?.size ?? 0} rowSpan="2">
              Lead Progression
              <span className="ml5 label label-info font-size-10">Conversion</span>
            </th>
          </tr>
          <tr className="col-headers">
            <th className="approaches" colSpan={approachesColumns?.size ?? 0}>
              Approaches
            </th>
            <th className="outreach-spacer" colSpan="2" />
          </tr>
          <tr className="col-headers">
            {approachesColumns?.map(column => (
              <th key={column.get('key')} title={column.get('title')}>
                {column.get('label')}
              </th>
            ))}
            {outreachLeafColumns?.map(column => (
              <th key={column.get('key')} rowSpan="2" title={column.get('title')}>
                {column.get('label')}
              </th>
            ))}
            {leadProgressionColumns?.map(column => (
              <th key={column.get('key')} rowSpan="2" title={column.get('title')}>
                {column.get('label')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{statistics?.get('modules')?.map(module => renderRow(module))}</tbody>
        <tfoot>
          <tr>
            <td className="text-uppercase text-bold name-column">Project Total</td>
            {sortedColumns?.map(column => (
              <td key={column.get('key')} className="text-right text-bold">
                {projectTotal?.get(camelize(column.get('key'))) ?? '—'}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export const FirmRollupTable = memo(FirmRollupTableComponent);
